import { useState, useEffect } from 'react';
import { 
  PictureAsPdf, 
  Description, 
} from '@mui/icons-material';
import styles from '../assets/scss/components/ui/StreamingMedia.module.scss';
import { useGetVehicleMediaWithUrl } from '../hooks/useReactQuery';
import PropTypes from "prop-types";
import NotFound from "../assets/img/Pagina_204.png";
import LoadingAnimation from '../components/loading/LoadingAnimation';

const VehicleStreamingMediaType = ({ url, type = 'vehiclePhoto', className = '' }) => {
  const [mediaContent, setMediaContent] = useState(null);
  const [mediaMetadata, setMediaMetadata] = useState(null);

  // Configuración mejorada del hook con cache específica
  const { 
    data: dataMediaWithUrl, 
    isLoading, 
    error,
    isFetching 
  } = useGetVehicleMediaWithUrl(url, type, {
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
    retry: 1,
    // Key específica para evitar colisiones de cache
    queryKey: ['vehicleMedia', url, type]
  });

  // Efecto para limpiar estado cuando cambia el URL o tipo
  useEffect(() => {
    // Resetear estado cuando cambie el vehículo
    setMediaContent(null);
    setMediaMetadata(null);
  }, [url, type]);

  // Efecto para manejar los datos recibidos
  useEffect(() => {
    if (dataMediaWithUrl) {
      if (dataMediaWithUrl.data && Object.keys(dataMediaWithUrl.data).length > 0) {
        setMediaContent(dataMediaWithUrl.data.content);
        setMediaMetadata({
          fileName: dataMediaWithUrl.data.fileName,
          fileType: dataMediaWithUrl.data.fileType,
          fileSize: dataMediaWithUrl.data.fileSize,
        });
      } else {
        // Si no hay datos válidos, asegurar que el estado esté limpio
        setMediaContent(null);
        setMediaMetadata(null);
      }
    } else if (error) {
      // En caso de error, limpiar estado
      setMediaContent(null);
      setMediaMetadata(null);
    }
  }, [dataMediaWithUrl, error]);

  const renderContent = () => {
    // Mostrar loading durante fetch inicial o refetch
    if (isLoading || isFetching) {
      return <LoadingAnimation />;
    }

    // Si hay error, respuesta 204, o no hay contenido
    if (error || 
        (!mediaContent && dataMediaWithUrl?.status === 204) || 
        (!mediaContent && !isLoading && !isFetching)) {
      return (
        <div className={styles.mediaContainer}>
          <img 
            src={NotFound} 
            alt="Imagen no disponible" 
            className={styles.image}
          />
        </div>
      );
    }

    if (!mediaMetadata?.fileType) {
      return (
        <div className={styles.mediaContainer}>
          <img 
            src={NotFound} 
            alt="Tipo de archivo no válido" 
            className={styles.image}
          />
        </div>
      );
    }

    switch (mediaMetadata.fileType.split('/')[0]) {
      case 'image':
        return (
          <img 
            src={mediaContent || NotFound} 
            alt={mediaMetadata.fileName || 'Imagen del vehículo'} 
            className={styles.image}
            onError={(e) => {
              e.target.src = NotFound;
            }}
          />
        );

      case 'video':
        return (
          <video 
            controls 
            src={mediaContent} 
            className={styles.video}
            onError={(e) => {
              console.error('Error loading video:', e);
            }}
          />
        );

      case 'application':
        return (
          <div className={styles.document}>
            {mediaMetadata.fileName?.endsWith('.pdf') ? (
              <PictureAsPdf />
            ) : (
              <Description />
            )}
            <p>{mediaMetadata.fileName}</p>
          </div>
        );

      default:
        return (
          <img 
            src={NotFound} 
            alt="Formato no soportado" 
            className={styles.image}
          />
        );
    }
  };

  return (
    <div className={`${styles.mediaContainer} ${className}`}>
      {renderContent()}
    </div>
  );
};

VehicleStreamingMediaType.propTypes = {
  url: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['vehiclePhoto', 'technicalReview', 'insurance', 'circulation']).isRequired,
  className: PropTypes.string,
};

export default VehicleStreamingMediaType;